import { assertPrivateKeyOrSigner } from '@exodus/asset'
import base32js from 'base32.js'
import assert from 'minimalistic-assert'

import { ON_COMPLETE_MAP } from './constants.js'
import { decodeAddress, encode, getAddressFromPublicKey } from './encode.js'
import { getPublicKey, sign } from './keypair.js'
import { assertSafeNumberAndStrictPostive, genericHash } from './utils.js'

const MAINNET = 'mainnet-v1.0'
const ALGORAND_MIN_TX_FEE = 1000 // version v5
const TAG = Buffer.from('TX')
/* Atomic Group Constants */
const GROUP_TAG = Buffer.from('TG')
const ALGORAND_MAX_TX_GROUP_SIZE = 16

/*
 firstRound: node lastRound
 lastRound: node lastRound + 1000
 This creates a 1000 block window for the transaction to be valid, which is the max.
*/

function prepareDataForSigning(txn, publicKey) {
  if (!txn.txObj.from) {
    // eslint-disable-next-line @exodus/mutable/no-param-reassign-prop-only
    txn.txObj.from = decodeAddress(getAddressFromPublicKey(publicKey))
  }

  return Buffer.concat([TAG, Buffer.from(encode(txn.getObjectForEncoding()))])
}

function prepareRawTx(txn, signature, publicKey) {
  const signedTx = { sig: signature, txn: txn.getObjectForEncoding() }

  // If the transaction is being signed by a keypair that isn't corresponding
  // to the keypair in the from, then we assum it's a rekeyed account
  // and we set the sgnr element.
  if (Buffer.compare(txn.txObj.from.publicKey, publicKey) !== 0) {
    signedTx.sgnr = publicKey
  }

  const encodedSignedTx = Buffer.from(encode(signedTx))

  return {
    txID: txn.getTxId().toString(),
    blob: encodedSignedTx,
  }
}

class Transaction {
  constructor({
    from,
    to,
    amount,
    fee = ALGORAND_MIN_TX_FEE,
    type = 'pay',
    group,
    firstRound,
    lastRound,
    genesisID = MAINNET,
    genesisHash,
    note = '',
    assetIndex, // token transfer
    rekeyTo,
    appId,
    appOnComplete,
    appArgs,
  } = {}) {
    assert(typeof type === 'string', 'type must be string')
    assert(fee, 'fee is required')
    assertSafeNumberAndStrictPostive(fee, 'fee')
    assert(firstRound, 'firstRound is required')
    assert(lastRound, 'lastRound is required')
    assertSafeNumberAndStrictPostive(firstRound, 'firstRound')
    assertSafeNumberAndStrictPostive(lastRound, 'lastRound')
    assert(firstRound < lastRound, 'lastRound must be bigger than firstRound')
    assert(genesisHash, 'genesisHash (base64 string) is required')

    // Leaving this out of the switch statement to retain backwards compatibility.
    if (assetIndex) {
      // token transfer
      assert(typeof assetIndex === 'number', 'assetIndex must be a number')
      type = 'axfer'
    }

    if (rekeyTo) {
      // Transaction to rekey
      assert(typeof rekeyTo === 'string', 'rekeyTo must be a string')
      rekeyTo = decodeAddress(rekeyTo)
    }

    if (from) {
      from = decodeAddress(from)
    }

    const txObj = {
      from,
      type,
      assetIndex,
      fee,
      firstRound,
      lastRound,
      genesisID,
      genesisHash: Buffer.from(genesisHash, 'base64'),
      note: Buffer.from(note),
      lease: Buffer.alloc(0),
      rekeyTo,
      group,
    }

    switch (type) {
      case 'pay':
      case 'axfer':
        assert(to, 'to is required')
        txObj.to = decodeAddress(to)
        assert(typeof amount === 'number', 'amount must be a number')
        assert(Number.isSafeInteger(amount) && amount >= 0, 'amount must be >= 0')
        txObj.amount = amount
        break
      case 'appl':
        assert(
          typeof appOnComplete === 'string',
          'appOnComplete must be provided when performing application calls'
        )
        assert(
          typeof appId === 'number',
          'appId must be provided when performing application calls'
        )
        if (appId) {
          assert(typeof appId === 'number', 'appId must be a number')
          txObj.appId = appId
        }

        if (appOnComplete) {
          assert(typeof appOnComplete === 'string', 'appOnComplete must be a string')
          assert(
            appOnComplete in ON_COMPLETE_MAP,
            `${appOnComplete} in not a valid oncomplete action`
          )
          appOnComplete = ON_COMPLETE_MAP[appOnComplete]
          // noop (= 0) should be undefined
          txObj.appOnComplete = appOnComplete > 0 ? appOnComplete : undefined
        }

        if (appArgs) {
          assert(Array.isArray(appArgs), 'appArgs must be an array')
          appArgs.forEach((elem, i) =>
            assert(
              elem.constructor && elem.constructor === Uint8Array,
              `appArgs[${i}] must be of type Uint8Array`
            )
          )
          txObj.appArgs = appArgs
        }

        break
      default:
        // Don't want to break any backwards compatbility
        // assert(false, `${type} invalid transaction type`)
        break
    }

    // Prevent this.txObj from being partially set and then failing by an assert()
    this.txObj = txObj
  }

  setGroupId(groupId) {
    this.txObj.group = groupId
  }

  getPlainTx() {
    return this.txObj
  }

  static async sign(txn, privateKey, signer) {
    assertPrivateKeyOrSigner(privateKey, signer)

    let signature, publicKey

    if (privateKey) {
      publicKey = getPublicKey(privateKey)
      signature = sign(prepareDataForSigning(txn, publicKey), privateKey)
    }

    if (signer) {
      publicKey = await signer.getPublicKey()
      signature = await signer.sign({
        data: prepareDataForSigning(txn, publicKey),
        signatureType: 'ed25519',
      })
    }

    return prepareRawTx(txn, signature, publicKey)
  }

  static computeGroupId(txns) {
    if (txns.length > ALGORAND_MAX_TX_GROUP_SIZE) {
      throw new Error('Too many transactions in single group')
    }

    const hashes = txns.map((txn) => txn.getRawTxId())
    const encodedHashes = encode({
      txlist: hashes,
    })

    const hashesBytes = Buffer.from(encodedHashes)
    return genericHash(Buffer.concat([GROUP_TAG, hashesBytes]))
  }

  getRawTxId() {
    const txBytes = Buffer.from(encode(this.getObjectForEncoding()))
    return genericHash(Buffer.concat([TAG, txBytes]))
  }

  getTxId() {
    const hash = this.getRawTxId()
    return base32js.encode(hash)
  }

  // https://github.com/algorand/js-algorand-sdk/blob/develop/src/transaction.ts#L452
  getObjectForEncoding() {
    const txObj = this.txObj
    const txn = {
      fee: txObj.fee,
      fv: txObj.firstRound,
      lv: txObj.lastRound,
      note: Buffer.from(txObj.note),
      snd: Buffer.from(txObj.from.publicKey),
      type: txObj.type,
      gen: txObj.genesisID,
      gh: txObj.genesisHash,
      lx: Buffer.from(txObj.lease),
      grp: txObj.group,
    }

    switch (txObj.type) {
      case 'pay': {
        // ALGO transfer
        txn.amt = txObj.amount
        if (txObj.to !== undefined) txn.rcv = Buffer.from(txObj.to.publicKey) // algo receiver

        break
      }

      case 'axfer': {
        // Token asset transfer or acceptance
        txn.aamt = txObj.amount
        txn.arcv = Buffer.from(txObj.to.publicKey) // asset receiver
        txn.xaid = txObj.assetIndex

        break
      }

      case 'appl': {
        // Application Call
        if (txObj.appId !== undefined) txn.apid = txObj.appId
        if (txObj.appOnComplete !== undefined) txn.apan = txObj.appOnComplete
        if (txObj.appArgs !== undefined) txn.apaa = txObj.appArgs

        break
      }
      // No default
    }

    // Support Rekey To Transactions
    if (txObj.rekeyTo !== undefined) txn.rekey = Buffer.from(txObj.rekeyTo.publicKey)

    // allowed zero values
    if (txn.note.length === 0) delete txn.note
    if (!txn.aamt) delete txn.aamt
    if (!txn.amt) delete txn.amt
    if (!txn.fee) delete txn.fee
    if (!txn.gen) delete txn.gen
    if (txn.grp === undefined) delete txn.grp
    if (txn.lx.length === 0) delete txn.lx

    return txn
  }
}

export default Transaction
